import React, { useContext, useEffect } from 'react'
import Context from '../../context'
import getConfig from 'next/config'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import { useChangeCartStore } from '../../hooks/cart.hooks'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import SVG from '../../components/SVG'
import { setPageViewData } from '../../utils/analytics'
import StoresMap from '../../components/StoresMap'
import AEMContent, { AEMPageType } from '../../components/AEMContent'
import { distance as getDistance } from '../../utils/distance'
dayjs.extend(customParseFormat)

const Store = ({ storeDetails, nearbyStores, storeLocatorDetails }) => {
  const router = useRouter()
  const { state } = useContext(Context)
  const changeCartCall = useChangeCartStore()
  const isPreview = storeDetails.store_preview
  const isClosed = storeDetails.is_closed
  const mapCenter = { lat: storeDetails.latitude, lng: storeDetails.longitude }

  const storeName = `${storeDetails.city}, ${storeDetails.state}`
  const [distance, setDistance] = React.useState(null)

  const replaceStorePlaceholder = (text) => {
    if (!text) return text
    return text.replace(/{{store\.name}}/g, storeName)
  }

  const sortedDetails = storeLocatorDetails ? [...storeLocatorDetails].sort((a, b) => a.index - b.index) : []
  const responsiveGrid = sortedDetails.find(item => item.type === 'responsive-grid')
  const storeTitles = sortedDetails.filter(item => item.type === 'storeheadertitle')
  const headerContent = storeTitles[0]
  const footerContent = storeTitles[1]
  const copyElements = sortedDetails.filter(item => item.type === 'copy')
  const copyContent = copyElements[copyElements.length - 1]

  useEffect(() => {
    const origin = Object.keys(state.location).length ? { lat: state.location.lat, lng: state.location.lng } : { lat: state.cart.store?.latitude, lng: state.cart.store?.longitude }
    setDistance(getDistance(origin.lat, origin.lng, storeDetails.latitude, storeDetails.longitude, 'M'))
    const path = `${storeDetails.state}/${storeDetails.city}/${storeDetails.storenumber}`
    setPageViewData(`store-directory/${path}`, 'Store', path)
  }, [state.location, state.cart.store])

  const handleGetDirections = () => {
    const fullAddress = `pOpshelf, ${storeDetails.address}, ${storeDetails.city}, ${storeDetails.state} ${storeDetails.postalcode}`
    const encodedAddress = encodeURIComponent(fullAddress)
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
    window.open(mapsUrl, '_blank')
  }

  const setStore = (store) => {
    changeCartCall(store.storenumber)
  }

  const renderStoreHours = () => {
    const jsDay = new Date().getDay()
    const dayList = [
      { label: 'Monday', value: `${dayjs(storeDetails.hours_mon.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_mon.split(':')[1], 'hhmm').format('h:mm A')}` },
      { label: 'Tuesday', value: `${dayjs(storeDetails.hours_tue.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_tue.split(':')[1], 'hhmm').format('h:mm A')}` },
      { label: 'Wednesday', value: `${dayjs(storeDetails.hours_wed.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_wed.split(':')[1], 'hhmm').format('h:mm A')}` },
      { label: 'Thursday', value: `${dayjs(storeDetails.hours_thu.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_thu.split(':')[1], 'hhmm').format('h:mm A')}` },
      { label: 'Friday', value: `${dayjs(storeDetails.hours_fri.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_fri.split(':')[1], 'hhmm').format('h:mm A')}` },
      { label: 'Saturday', value: `${dayjs(storeDetails.hours_sat.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_sat.split(':')[1], 'hhmm').format('h:mm A')}` },
      { label: 'Sunday', value: `${dayjs(storeDetails.hours_sun.split(':')[0], 'hhmm').format('h:mm A')} - ${dayjs(storeDetails.hours_sun.split(':')[1], 'hhmm').format('h:mm A')}` }
    ]
    return dayList.map((day, idx) => {
      const isToday = (jsDay === 0 ? 6 : jsDay - 1) === idx
      return (
        <div className={`store-detail__hours-row${isToday ? ' store-detail__hours-row--today' : ''}`} key={day.label}>
          <span className={`store-detail__hours-entry${isToday ? ' store-detail__hours-entry--today' : ''}`}>{day.label}</span>
          <span className={`store-detail__hours-entry${isToday ? ' store-detail__hours-entry--today' : ''}`}>{day.value}</span>
        </div>
      )
    })
  }

  return (
    <div className="store-detail">
      <Head>
        <title>Visit pOpshelf in {storeDetails.city}, {storeDetails.state}</title>
        <meta name="description" content={storeDetails.address} />
      </Head>
      <div className="store-detail__mobile-title">
        {Object.keys(storeDetails).length !== 0 &&
          <>
            {`${storeDetails.city}, ${storeDetails.state} `}
            <span className="store-detail__mobile-title-number">#{storeDetails.storenumber}</span>
          </>
        }
      </div>
      {distance && (
        <div className="store-detail__mobile-distance">
          {distance.toFixed(2)} miles away
        </div>
      )}
      <div className="store-detail__mobile-map">
        <StoresMap
          center={mapCenter}
          stores={[storeDetails]}
          selectedStoreId={storeDetails.storenumber}
          onStoreClick={() => { }}
        />
      </div>
      <div className="store-detail__header">
        <div className="store-detail__header-title">
          <div className="store-detail__header-logo">
            <SVG name='logo-color' label='logo' size='xl' />
          </div>
          <div className="store-detail__header-info">
            <label>
              {Object.keys(storeDetails).length !== 0 &&
                <>
                  {`${storeDetails.city}, ${storeDetails.state} `}
                  <span className="store-detail__header-store-number">#{storeDetails.storenumber}</span>
                </>
              }
            </label>
          </div>
        </div>
      </div>
      <div className="store-detail__container">
        <div className="store-detail__container-details">
          <div className="store-detail__single-panel">
            <div className="store-detail__info-content">
              <div className="store-detail__info-address" title={storeDetails.address}>{`${storeDetails.address}`}</div>
              {!isPreview && storeDetails.phone && (
                <a
                  href={`tel:${storeDetails.phone}`}
                  className="store-detail__info-phone"
                >
                  <SVG name="phone" label="phone" size="base" />
                  {(() => {
                    const cleaned = storeDetails.phone.replace(/\D/g, '')
                    if (cleaned.length === 10) {
                      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
                    }
                    return storeDetails.phone
                  })()}
                </a>
              )}
              <button
                onClick={handleGetDirections}
                className="store-detail__info-distance"
              >
                <SVG name="icon-store-location" label="store location" size="base" />
                Directions
              </button>
              {!isPreview && (
                <div className="store-detail__info-hours">
                  <SVG name="clock" label="clock" size="base" />
                  <span className="store-detail__info-hours-text">
                    {getClosingTime(storeDetails)}
                  </span>
                </div>
              )}
              {state.cart.store?.storenumber === storeDetails.storenumber && (
                <div className="store-detail__your-store-badge">
                  <SVG name="checkmark-white" label="checkmark-white" size="base" />
                  <span>your store</span>
                </div>
              )}
            </div>
            <div className="store-detail__action-content">
              {!isPreview && !isClosed && state.cart.store?.storenumber !== storeDetails.storenumber && (
                <>
                  <button
                    className="btn__primary store-detail__set-store-btn store-detail__set-store-btn--mobile"
                    onClick={() => setStore(storeDetails)}
                  >
                    SELECT AS YOUR STORE
                  </button>
                  <button
                    className="btn__secondary store-detail__set-store-btn store-detail__set-store-btn--desktop"
                    onClick={() => setStore(storeDetails)}
                  >
                    Make my store
                  </button>
                </>
              )}
              {isPreview && <label className="store-detail__message-text">coming soon!</label>}
              {isClosed && <label className="store-detail__message-text store-detail__message-text--error">temporarily closed</label>}
            </div>
          </div>
          <div className="store-detail__art-image store-detail__art-image--desktop">
            <Image src="/images/store-art.jpg" alt="Store" width={340} height={185} style={{ width: '100%', objectFit: 'contain' }} />
          </div>
          {!isPreview && (
            <div className="store-detail__hours-section">
              <div className="store-detail__hours-label">store hours:</div>
              <div className="store-detail__hours-table" style={{ marginTop: '5px' }}>
                {renderStoreHours()}
              </div>

            </div>
          )}
          {!isPreview && (
            <div className="store-detail__services-section">
              <div className="store-detail__services-title">store services:</div>
              <div className="store-detail__services-list">
                <div className="store-detail__service-item">
                  <SVG name="free-helium" label="Free Helium" size="sm" />
                  <span>free helium</span>
                </div>
                {storeDetails.store_service_names?.includes('BOPIS') && (
                  <div className="store-detail__service-item">
                    <SVG name="store-pickup" label="Store Pickup" size="sm" />
                    <span>store pickup</span>
                  </div>
                )}
                <div className="store-detail__service-item">
                  <SVG name="doordash" label="DoorDash Delivery" size="sm" />
                  <span>DoorDash delivery</span>
                </div>
              </div>
            </div>
          )}
          <div className="store-detail__art-image store-detail__art-image--mobile">
            <img src="/images/store-art.jpg" alt="Store" style={{ width: '100%' }} />
          </div>

        </div>
        <div className="store-detail__container-map">
          <StoresMap
            center={mapCenter}
            stores={[storeDetails]}
            selectedStoreId={storeDetails.storenumber}
            onStoreClick={() => { }}
          />
        </div>
      </div>
      {nearbyStores && nearbyStores.length > 0 && (
        <div className="store-detail__nearby">
          <h3 className="store-detail__nearby-title">stores nearby:</h3>
          <div className="store-detail__nearby-list">
            {nearbyStores.map((store) => {
              const isCurrentStore = state.cart.store?.storenumber === store.storenumber
              const storeIsPreview = store.store_preview
              const storeIsClosed = store.is_closed
              return (
                <div key={store.storenumber} className="store-detail__nearby-item">
                  <div className="store-detail__nearby-item-content">
                    <div className="store-detail__nearby-item-label">{store.city}, {store.state}</div>
                    <div className="store-detail__nearby-item-address" title={store.address}>{store.address}</div>
                    {store.distance && (
                      <div className="store-detail__nearby-item-distance">
                        <SVG name="icon-store-location" label="store location" size="base" />
                        {store.distance.toFixed(2)} miles away
                      </div>
                    )}
                    {!storeIsPreview && (
                      <div className="store-detail__nearby-item-hours">
                        <SVG name="clock" label="clock" size="base" />
                        <span>
                          {getClosingTime(store)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="store-detail__nearby-item-actions">
                    <button
                      className="btn__secondary store-detail__nearby-item-details"
                      onClick={() => router.push(`/store/${store.storenumber}`)}
                    >
                      STORE DETAILS
                    </button>
                    {!storeIsPreview && !storeIsClosed && !isCurrentStore && (
                      <button
                        className="store-detail__nearby-item-select"
                        onClick={() => changeCartCall(store.storenumber)}
                      >
                        make my store
                      </button>
                    )}
                    {isCurrentStore && (
                      <div className="store-detail__nearby-item-badge">
                        <SVG name="checkmark-white" label="checkmark-white" size="base" />
                        <span>your store</span>
                      </div>
                    )}
                    {storeIsPreview && <label className="store-detail__nearby-item-message">coming soon!</label>}
                    {storeIsClosed && <label className="store-detail__nearby-item-message store-detail__nearby-item-message--error">temporarily closed</label>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {headerContent && (
        <div className="store-detail__main-content">
          <h1>
            {replaceStorePlaceholder(headerContent.title)}
          </h1>
        </div>
      )}
      {responsiveGrid && (
        <div className="store-detail__aem-content">
          <AEMContent template={responsiveGrid.template} page={AEMPageType.stores} />
        </div>
      )}
      {footerContent && (
        <div className="store-detail__footer-content">
          <h2>
            {replaceStorePlaceholder(footerContent.title)}
          </h2>
        </div>
      )}
      {copyContent && (
        <div className="store-detail__copy-content">
          <div dangerouslySetInnerHTML={{ __html: replaceStorePlaceholder(copyContent.value) }} />
        </div>
      )}
    </div>
  )
}

export default Store

const getClosingTime = (store) => {
  const dayName = dayjs().format('ddd').toLowerCase()
  const closing = store[`hours_${dayName}`].split(':')[1]
  return `Closes at ${dayjs(closing, 'hhmm').format('ha')} today`
}

export const getServerSideProps = async ({ query }) => {
  const { publicRuntimeConfig } = getConfig()
  const res = await fetch(`${publicRuntimeConfig.APP_ENDPOINT}/api/stores/${publicRuntimeConfig.ENV}/store?id=${query.id}&popkey=${publicRuntimeConfig.POP_KEY}`)
  const data = await res.json()
  const storeDetails = data[0]

  const nearbyRes = await fetch(`${publicRuntimeConfig.APP_ENDPOINT}/api/stores/${publicRuntimeConfig.ENV}/store?lat=${storeDetails.latitude}&lng=${storeDetails.longitude}&popkey=${publicRuntimeConfig.POP_KEY}`)
  const nearbyStores = await nearbyRes.json()

  const filteredNearbyStores = nearbyStores.filter(store => store.storenumber !== storeDetails.storenumber).slice(0, 4)

  const aemRes = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/stores/${publicRuntimeConfig.ENV}/store-locator-template?popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        'X-Pop-App-Token': publicRuntimeConfig.POP_TOKEN_WEB
      }
    }
  )
  const aemData = await aemRes.json()

  const storeLocatorDetails = aemData.data.store_locator_details

  return {
    props: {
      storeDetails,
      nearbyStores: filteredNearbyStores,
      storeLocatorDetails: storeLocatorDetails
    }
  }
}
